import { useLayoutEffect, type MutableRefObject } from 'react';
import type * as am5xy from '@amcharts/amcharts5/xy';
import type { SensorData } from './analysisTabSensorTypes';
import { getGroupChartUnitContext, getSelectedSensorData } from './analysisTabChartData';
import { buildGroupAnalysisChart, shouldBuildGroupChart } from './analysisTabGroupChartBuild';

export interface UseAnalysisGroupChartParams {
  chartMode: 'individual' | 'group';
  selectedSensors: string[];
  sensors: SensorData[];
  chartDataSnapshot: SensorData[];
  hasValidChartData: boolean;
  hasNoData: boolean;
  initialDataLoadComplete: boolean;
  chartDataLoading: boolean;
  customScaling: { [unit: string]: { min: number; max: number } } | null;
  uomRanges: { uom: string; minValue: number; maxValue: number }[];
  chartDataKey: string;
  chartCreated: boolean;
  lastChartDataKey: string;
  getDateRange: () => { startTime: string; endTime: string };
  chartInstanceRef: MutableRefObject<am5xy.XYChart | null>;
  chartCreationRef: MutableRefObject<boolean>;
  chartCreationTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  setChartCreated: (value: boolean) => void;
  setLastChartDataKey: (value: string) => void;
}

export function useAnalysisGroupChart(params: UseAnalysisGroupChartParams): void {
  const {
    chartMode,
    selectedSensors,
    sensors,
    chartDataSnapshot,
    hasValidChartData,
    hasNoData,
    initialDataLoadComplete,
    chartDataLoading,
    customScaling,
    uomRanges,
    chartDataKey,
    chartCreated,
    lastChartDataKey,
    getDateRange,
    chartInstanceRef,
    chartCreationRef,
    chartCreationTimeoutRef,
    setChartCreated,
    setLastChartDataKey,
  } = params;

  useLayoutEffect(() => {
    if (chartMode !== 'group') {
      return;
    }

    const selectedData = getSelectedSensorData(sensors, selectedSensors, chartDataSnapshot, hasValidChartData);
    const { visibleSensorsWithData, uniqueUnits, hasDataToDisplay } = getGroupChartUnitContext(selectedData);

    if (hasNoData && initialDataLoadComplete) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.root.dispose();
        chartInstanceRef.current = null;
        setChartCreated(false);
        setLastChartDataKey('');
      }
      return;
    }

    const canBuild = shouldBuildGroupChart(
      uniqueUnits,
      selectedData.length,
      hasDataToDisplay,
      hasNoData,
      chartDataLoading,
      initialDataLoadComplete
    );

    if (!canBuild) {
      return;
    }

    if (chartCreated && chartInstanceRef.current && chartDataKey === lastChartDataKey) {
      setTimeout(() => {
        if (chartInstanceRef.current?.root) {
          chartInstanceRef.current.root.resize();
        }
      }, 100);
      return;
    }

    if (chartCreationRef.current) {
      return;
    }

    if (chartCreationTimeoutRef.current) {
      clearTimeout(chartCreationTimeoutRef.current);
    }

    chartCreationTimeoutRef.current = setTimeout(() => {
      chartCreationRef.current = true;
      const containerElement = document.getElementById('analysis-chartdiv');
      if (!containerElement) {
        chartCreationRef.current = false;
        return;
      }

      if (chartInstanceRef.current && chartDataKey !== lastChartDataKey) {
        chartInstanceRef.current.root.dispose();
        chartInstanceRef.current = null;
        setChartCreated(false);
      }

      if (chartInstanceRef.current && chartDataKey === lastChartDataKey) {
        chartCreationRef.current = false;
        return;
      }

      const { chart } = buildGroupAnalysisChart({
        containerId: 'analysis-chartdiv',
        visibleSensorsWithData,
        uniqueUnits,
        hasDataToDisplay,
        customScaling,
        uomRanges,
        sensors,
        getDateRange,
      });

      chartInstanceRef.current = chart;
      chartCreationRef.current = false;
      setChartCreated(true);
      setLastChartDataKey(chartDataKey);
    }, 200);
  }, [selectedSensors, hasNoData, initialDataLoadComplete, customScaling, chartMode]);
}
